import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APIResponse } from '@macs-frontend/web-macs/shared';
import { Observable } from 'rxjs';
import { publishReplay, refCount } from 'rxjs/operators';
import { UUID } from '../utils/uuid';
import { ApiService } from './api.service';

interface CachedKeyGroups {
  [group: string]: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DropdownApiService extends ApiService {
  private cacheKey = 'dropdown-list_';
  private cache = new Map<string, Observable<APIResponse<any[]>>>();

  private cachedKeyGroups: CachedKeyGroups = {};

  constructor(protected http: HttpClient) {
    super(http);
  }

  getList(
    url: string,
    group?: string,
    headers?: {
      readonly [header: string]: string | readonly string[] | UUID | readonly UUID[];
    },
  ): Observable<APIResponse<any[]>> {
    const key = this.cacheKey + url;
    const cachedList = this.cache.get(key);
    if (cachedList) {
      return cachedList;
    }

    const observable = this.http
      .request<APIResponse<any[]>>('GET', url, {
        headers: headers as { readonly [header: string]: string | string[] },
      })
      .pipe(publishReplay(1), refCount());

    this.cache.set(key, observable);

    if (group) {
      if (!this.cachedKeyGroups[group]) {
        this.cachedKeyGroups[group] = [];
      }
      this.cachedKeyGroups[group].push(key);
    }

    return observable;
  }

  invalidateCache(groups?: string[]) {
    // eslint-disable-next-line no-param-reassign
    groups = groups || Object.keys(this.cachedKeyGroups);
    groups.forEach(group => this.removeCacheGroup(group));
    this.removeFromCachedKeyGroups(groups);
  }

  private removeFromCachedKeyGroups(groups: string[]) {
    this.cachedKeyGroups = Object.keys(this.cachedKeyGroups).reduce(
      (newGroups: CachedKeyGroups, group: string) => {
        if (!groups.includes(group)) {
          newGroups[group] = this.cachedKeyGroups[group];
        }
        return newGroups;
      },
      {},
    );
  }

  private removeCacheGroup(group: string) {
    if (this.cachedKeyGroups[group]) {
      this.cachedKeyGroups[group].forEach(cachedKey => this.cache.delete(cachedKey));
    }
  }
}
